import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';
import Icon from '../../../components/AppIcon';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    rememberMe: false
  });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loginError, setLoginError] = useState(null);
  const [redirectMessage, setRedirectMessage] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  // Check for redirect message from billing dashboard
  useEffect(() => {
    if (location?.state?.message) {
      setRedirectMessage(location?.state?.message);
    }
  }, [location]);

  const mockCredentials = {
    email: 'agent@offercompare.com',
    password: 'Agent@2026'
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex?.test(email);
  };

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (errors?.[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    if (loginError) {
      setLoginError('');
    }
  };

  const handleCheckboxChange = (checked) => {
    setFormData(prev => ({
      ...prev,
      rememberMe: checked
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.password) {
      newErrors.password = 'Password is required';
    } else if (formData?.password?.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return Object?.keys(newErrors)?.length === 0;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoginError(null);

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setIsLoading(true);

    try {
      const { error } = await signIn({
        email: formData?.email,
        password: formData?.password
      });

      if (error) {
        // Check if it's an email verification error
        const errorMessage = error?.message || error || 'Failed to sign in';
        
        if (errorMessage?.toLowerCase()?.includes('email not confirmed') || 
            errorMessage?.toLowerCase()?.includes('email confirmation') ||
            errorMessage?.toLowerCase()?.includes('verify your email')) {
          setLoginError('Please verify your email address before logging in. Check your inbox for the verification link.');
        } else {
          setLoginError(errorMessage);
        }
        
        setIsSubmitting(false);
        setIsLoading(false);
        return;
      }

      // Wait briefly to ensure session is fully synchronized
      // This prevents race conditions when user immediately clicks "Upgrade to Pro"
      await new Promise(resolve => setTimeout(resolve, 300));

      // Redirect to the page they came from or default to dashboard
      const redirectTo = location?.state?.from || '/agent-dashboard';
      navigate(redirectTo);
    } catch (error) {
      console?.error('Login error:', error);
      setLoginError(error?.message || 'An unexpected error occurred. Please try again.');
      setIsSubmitting(false);
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 md:space-y-5 lg:space-y-6">
      {redirectMessage && (
        <div className="p-3 md:p-4 bg-primary/10 border border-primary/20 rounded-lg flex items-start gap-2 md:gap-3">
          <Icon name="Info" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-primary">{redirectMessage}</p>
          </div>
        </div>
      )}
      {loginError && (
        <div className="p-3 md:p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-2 md:gap-3">
          <Icon name="AlertCircle" size={20} color="var(--color-destructive)" className="flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm text-destructive">{loginError}</p>
          </div>
        </div>
      )}

      <div className="space-y-4 md:space-y-5">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-foreground mb-2">
            Email Address
          </label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="agent@example.com"
            value={formData?.email}
            onChange={handleInputChange}
            error={errors?.email}
            disabled={isSubmitting}
            icon="Mail"
            autoComplete="email"
          />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label htmlFor="password" className="block text-sm font-medium text-foreground">
              Password
            </label>
            <a 
              href="/forgot-password" 
              className="text-sm text-primary hover:text-primary/80 transition-colors font-medium"
            >
              Forgot Password?
            </a>
          </div>
          <div className="relative">
            <Input
              id="password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Enter your password"
              value={formData?.password}
              onChange={handleInputChange}
              error={errors?.password}
              disabled={isSubmitting}
              icon="Lock"
              autoComplete="current-password"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              tabIndex={-1}
            >
              <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={20} />
            </button>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Checkbox
              id="rememberMe"
              checked={formData?.rememberMe}
              onCheckedChange={handleCheckboxChange}
              disabled={isSubmitting}
            />
            <label
              htmlFor="rememberMe"
              className="text-sm text-foreground cursor-pointer select-none"
            >
              Remember me
            </label>
          </div>
        </div>
      </div>

      <div className="space-y-3 md:space-y-4 pt-2">
        <Button
          type="submit"
          variant="outline"
          size="lg"
          fullWidth
          disabled={isSubmitting}
          loading={isLoading}
        >
          {isLoading ? 'Signing In...' : 'Sign In'}
        </Button>

        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border"></div>
          </div>
          <div className="relative flex justify-center text-xs md:text-sm">
            <span className="px-2 bg-card text-muted-foreground">New to OfferSense?</span>
          </div>
        </div>

        <Button
          type="button"
          variant="outline"
          size="lg"
          fullWidth
          onClick={() => navigate('/user-registration')}
          disabled={isSubmitting}
        >
          Create Account
        </Button>
      </div>
    </form>
  );
};

export default LoginForm;